import { useEffect, useState } from 'react'
import { Loader2, RotateCw } from 'lucide-react'
import { Drawer, DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { StatusBadge } from '@/components/erp/primitives'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { useCan } from '@/shared/auth/session'
import { fmtDateTimeSec, humanise } from '@/shared/lib/format'
import { notificationDetail, retryNotification } from './api'
import type { NotificationDetailResponse, NotificationSummaryResponse } from '@/shared/api/types'

/**
 * Message detail, including the body that was actually sent.
 *
 * The body is rendered as ESCAPED TEXT, never with `dangerouslySetInnerHTML`.
 * These are provider-bound HTML payloads and some are assembled from
 * user-supplied fields; injecting them into this document would make the
 * notification log an XSS surface for anyone who can trigger a send.
 * The backend already redacts invitation and reset tokens before storing.
 */
export function NotificationDetailDrawer({
  message, onClose, onChanged,
}: {
  message: NotificationSummaryResponse | null
  onClose: () => void
  onChanged: () => void
}) {
  const canSend = useCan('notification:send')
  const [full, setFull] = useState<NotificationDetailResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null)

  useEffect(() => {
    setFull(message ? { ...message } as NotificationDetailResponse : null)
    setError(null)
    setNote(null)
    if (!message) return
    let cancelled = false
    setLoading(true)
    notificationDetail(message.id)
      .then((m) => { if (!cancelled) setFull(m) })
      .catch((err) => { if (!cancelled) setError(explain(err)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [message])

  async function retry() {
    if (!full) return
    setBusy(true)
    setNote(null)
    try {
      const updated = await retryNotification(full.id)
      setFull(updated)
      setNote({ tone: 'good', text: `Retried — status is now ${humanise(updated.status)}.` })
      onChanged()
    } catch (err) {
      setNote({ tone: 'bad', text: explain(err) })
    } finally {
      setBusy(false)
    }
  }

  const m = full
  const retryable = canSend && m?.status === 'FAILED'

  return (
    <Drawer
      open={Boolean(message)}
      onClose={onClose}
      title={m?.subject || 'Message'}
      subtitle={m ? `${m.recipient} · ${fmtDateTimeSec(m.createdAt)}` : undefined}
      right={m ? <StatusBadge status={m.status} /> : undefined}
      width="lg"
      footer={retryable ? (
        <button className="erp-btn erp-btn-primary" onClick={retry} disabled={busy}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
          Retry send
        </button>
      ) : undefined}
    >
      <ErrorStrip message={error} />
      {loading && !m && (
        <div className="px-4 py-10 text-center text-erp-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
        </div>
      )}

      {m && (
        <>
          {note && (
            <div className="p-4 pb-0">
              <Note tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Note>
            </div>
          )}

          {m.status === 'FAILED' && m.error && (
            <div className="p-4 pb-0">
              <Note tone="bad">
                <span className="font-medium">Delivery failed.</span>{' '}
                <span className="break-words">{m.error}</span>
              </Note>
            </div>
          )}

          <DrawerSection title="Delivery">
            <DrawerField label="Status" value={<StatusBadge status={m.status} />} />
            <DrawerField label="Channel" value={humanise(m.channel)} />
            <DrawerField label="Type" value={humanise(m.type)} />
            <DrawerField label="Attempts" value={<span className="erp-metric">{m.attempts}</span>} />
            <DrawerField label="Queued" value={fmtDateTimeSec(m.queuedAt)} />
            <DrawerField label="Last attempt" value={fmtDateTimeSec(m.lastAttemptAt)} />
            <DrawerField label="Sent" value={fmtDateTimeSec(m.sentAt)} />
          </DrawerSection>

          <DrawerSection title="Recipient">
            <DrawerField label="Address" value={m.recipient} />
            <DrawerField label="User ID" value={m.recipientUserId} mono />
          </DrawerSection>

          {/* Provenance is what makes a failed message actionable — it answers
              "which action sent this?" without a log dive. */}
          <DrawerSection title="Provenance">
            <DrawerField label="Entity type" value={m.entityType} mono />
            <DrawerField label="Entity ID" value={m.entityId} mono />
            <DrawerField label="Request ID" value={m.requestId} mono />
            <DrawerField label="Sent by" value={m.createdBy} mono />
            <DrawerField label="Message ID" value={m.id} mono />
          </DrawerSection>

          <DrawerSection title="Body">
            {m.bodyHtml ? (
              <div className="px-4 py-3">
                <p className="text-erp-xs text-muted-foreground mb-2">
                  Shown as source. Invitation and reset tokens are redacted before storage.
                </p>
                <pre className="text-erp-xs font-mono bg-muted/40 border border-border rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-words max-h-96">
                  {m.bodyHtml}
                </pre>
              </div>
            ) : (
              <p className="px-4 py-3 text-erp-sm text-muted-foreground">Body not available.</p>
            )}
          </DrawerSection>
        </>
      )}
    </Drawer>
  )
}
